import {
	ActionIcon,
	Alert,
	Button,
	Card,
	Group,
	Loader,
	Stack,
	Text,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
	IconFingerprint,
	IconInfoCircle,
	IconKey,
	IconPlus,
	IconTrash,
} from "@tabler/icons-react";
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseQueryResult,
} from "@tanstack/react-query";
import type { AuthWebAuthnCredential } from "aws-amplify/auth";
import { useEffect, useRef } from "react";

import { useLogger } from "@/logging/client/LoggerContext";
import { useFreshAuth } from "../hooks/useFreshAuth";
import { useUserMFAStatus } from "../hooks/useUserMFAStatus";
import {
	useAmplifyAuth,
	type AmplifyAuthFunctions,
} from "./AmplifyAuthContext";
import { FreshAuthRequired } from "./FreshAuthRequired";
import { PasskeyErrorMessage } from "./PasskeyErrorMessage";

const passkeysQueryKey = ["userPasskeys"];

// Cognito caps ListWebAuthnCredentials at 20 per page.
const passkeysPageSize = 20;

export function PasskeyManagement() {
	const queryClient = useQueryClient();
	const logger = useLogger();
	const {
		associateWebAuthnCredential,
		listWebAuthnCredentials,
		deleteWebAuthnCredential,
		updatePasskeyMFAPreference,
	} = useAmplifyAuth();
	const freshAuth = useFreshAuth();
	const mfaStatus = useUserMFAStatus();

	const passkeysQuery = useQuery({
		queryKey: passkeysQueryKey,
		queryFn: async () => {
			logger.info("PasskeyManagement: listPasskeys");
			try {
				const passkeys = await listAllPasskeys(listWebAuthnCredentials);
				logger.info(
					`PasskeyManagement: listPasskeys - success - ${passkeys.length} passkeys`,
				);
				return passkeys;
			} catch (error) {
				logger.error(
					`PasskeyManagement: listPasskeys - error - ${error instanceof Error ? error.message : "unknown error"}`,
				);
				throw error;
			}
		},
		enabled: freshAuth.isFresh,
	});

	const { mutate: enablePasskeyMFA } = useMutation({
		mutationFn: () => {
			logger.info("PasskeyManagement: enablePasskeyMFA");
			return updatePasskeyMFAPreference("ENABLED");
		},
		onError: (error: Error) => {
			logger.error(
				`PasskeyManagement: enablePasskeyMFA - error - ${error.message}`,
			);
		},
	});

	// ensure passkey MFA pref is set in-case of partial failures during passkey add
	// or other account configuration mishaps. otherwise the user can't login with passkey and would have
	// no recouse other than esclating to IT/Dev to manually set it in Cognito console.
	const hasCheckedPasskeyMFAOnOpen = useRef(false);
	const hasOtherMFA = mfaStatus.hasSMS || mfaStatus.hasTOTP;
	const hasPasskeys = (passkeysQuery.data?.length ?? 0) > 0;
	const isReadyToCheckPasskeyMFA =
		!mfaStatus.isLoading && passkeysQuery.isSuccess;
	useEffect(() => {
		if (hasCheckedPasskeyMFAOnOpen.current || !isReadyToCheckPasskeyMFA) {
			return;
		}
		hasCheckedPasskeyMFAOnOpen.current = true;
		if (hasOtherMFA && hasPasskeys) {
			enablePasskeyMFA();
		}
	}, [isReadyToCheckPasskeyMFA, hasOtherMFA, hasPasskeys, enablePasskeyMFA]);

	const addPasskeyMutation = useMutation({
		mutationFn: async () => {
			logger.info("PasskeyManagement: addPasskey");
			await associateWebAuthnCredential();
			// Cognito only offers passkey sign-in to users with passkey MFA enabled.
			await updatePasskeyMFAPreference("ENABLED");
		},
		onSuccess: () => {
			logger.info("PasskeyManagement: addPasskey - success");
			notifications.show({
				title: "Passkey Added",
				message: "You can now sign in with this passkey",
				color: "green",
			});
		},
		onError: (error: Error) => {
			logger.error(
				`PasskeyManagement: addPasskey - error - ${serializeError(error)}`,
			);
			notifications.show({
				title: "Error adding passkey",
				message: <PasskeyErrorMessage error={error} />,
				color: "red",
			});
		},
		// The passkey may be registered even if enabling passkey MFA failed.
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: passkeysQueryKey });
		},
	});

	const removePasskeyMutation = useMutation({
		mutationFn: (credentialId: string) => {
			logger.info(`PasskeyManagement: removePasskey - ${credentialId}`);
			return deleteWebAuthnCredential({ credentialId });
		},
		onSuccess: (_result, credentialId) => {
			logger.info(
				`PasskeyManagement: removePasskey - success - ${credentialId}`,
			);
			queryClient.invalidateQueries({ queryKey: passkeysQueryKey });
			notifications.show({
				title: "Passkey Removed",
				message: "The passkey can no longer be used to sign in",
				color: "green",
			});
		},
		onError: (error: Error, credentialId) => {
			logger.error(
				`PasskeyManagement: removePasskey - error - ${credentialId} - ${error.message}`,
			);
			notifications.show({
				title: "Error removing passkey",
				message: error.message,
				color: "red",
			});
		},
	});

	const addPasskey = async () => {
		if (await freshAuth.ensureFreshAuth()) {
			addPasskeyMutation.mutate();
		}
	};

	const confirmRemovePasskey = (credentialId: string, name: string) => {
		modals.openConfirmModal({
			title: "Remove Passkey",
			children: (
				<Text size="sm">
					Are you sure you want to remove <strong>{name}</strong>? You will no
					longer be able to sign in with it.
				</Text>
			),
			labels: { confirm: "Remove", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				if (await freshAuth.ensureFreshAuth()) {
					removePasskeyMutation.mutate(credentialId);
				}
			},
		});
	};

	if (freshAuth.needsFreshAuth) {
		return <FreshAuthRequired />;
	}

	return (
		<Stack gap="sm">
			<Text size="xs" c="dimmed">
				Passkeys let you sign in with your device's fingerprint, face, or screen
				lock instead of a password.
			</Text>

			<PasskeyList
				query={passkeysQuery}
				isRemoving={removePasskeyMutation.isPending}
				onRemove={confirmRemovePasskey}
			/>

			<Group justify="flex-end">
				<Button
					variant="light"
					leftSection={<IconPlus size={16} />}
					onClick={addPasskey}
					loading={addPasskeyMutation.isPending}
				>
					Add Passkey
				</Button>
			</Group>
		</Stack>
	);
}

interface PasskeyListProps {
	query: UseQueryResult<AuthWebAuthnCredential[], Error>;
	isRemoving: boolean;
	onRemove: (credentialId: string, name: string) => void;
}

// isPending rather than isLoading, so the list also shows loading while the
// query waits on the fresh-auth check.
function PasskeyList({ query, isRemoving, onRemove }: PasskeyListProps) {
	if (query.isPending) {
		return (
			<Card withBorder p="md">
				<Group>
					<Loader size="sm" />
					<Text size="sm">Loading passkeys...</Text>
				</Group>
			</Card>
		);
	}

	if (query.error) {
		return (
			<Alert color="red" icon={<IconInfoCircle size={16} />}>
				<Text size="sm">Failed to load passkeys: {query.error.message}</Text>
			</Alert>
		);
	}

	if (!query.data || query.data.length === 0) {
		return (
			<Card withBorder p="md">
				<Group>
					<IconFingerprint size={20} color="gray" />
					<Text size="sm" c="dimmed">
						No passkeys registered
					</Text>
				</Group>
			</Card>
		);
	}

	return (
		<Stack gap="xs">
			{query.data.map((credential) => {
				const credentialId = credential.credentialId;
				const name = credential.friendlyCredentialName ?? "Unnamed passkey";
				return (
					<Card key={credentialId} withBorder p="md">
						<Group justify="space-between" wrap="nowrap">
							<Group wrap="nowrap">
								<IconKey size={20} />
								<Stack gap={0}>
									<Text size="sm" fw={500}>
										{name}
									</Text>
									{credential.createdAt && (
										<Text size="xs" c="dimmed">
											Added {credential.createdAt.toLocaleDateString()}
										</Text>
									)}
								</Stack>
							</Group>
							{credentialId && (
								<ActionIcon
									variant="subtle"
									color="red"
									aria-label={`Remove ${name}`}
									title={`Remove ${name}`}
									disabled={isRemoving}
									onClick={() => onRemove(credentialId, name)}
								>
									<IconTrash size={16} />
								</ActionIcon>
							)}
						</Group>
					</Card>
				);
			})}
		</Stack>
	);
}

async function listAllPasskeys(
	listWebAuthnCredentials: AmplifyAuthFunctions["listWebAuthnCredentials"],
): Promise<AuthWebAuthnCredential[]> {
	const credentials: AuthWebAuthnCredential[] = [];
	let nextToken: string | undefined = undefined;
	do {
		const page = await listWebAuthnCredentials({
			pageSize: passkeysPageSize,
			nextToken,
		});
		credentials.push(...page.credentials);
		nextToken = page.nextToken;
	} while (nextToken);
	return credentials;
}

// Error name, message and cause are non-enumerable, so JSON.stringify drops
// them. Amplify nests the native plugin error (with its code) in underlyingError.
function serializeError(error: Error): string {
	return JSON.stringify(error, (_key, value) =>
		value instanceof Error
			? {
					...value,
					name: value.name,
					message: value.message,
					cause: value.cause,
				}
			: value,
	);
}
